import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Tag, Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

const PromoCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '', type: 'percentage', value: 0, description: '', minPurchase: 0, maxUses: 999999, stackable: false, expiresAt: ''
  });
  const { toast } = useToast();

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/promo-codes', { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setCodes(await res.json());
    } catch { toast({ title: "Error", description: "Failed to load promo codes.", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const createCode = async (e) => {
    e.preventDefault();
    if (!newCode.code.trim()) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/promo-codes', {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newCode, expiresAt: newCode.expiresAt || null })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      toast({ title: "Created!", description: `Promo code "${newCode.code.toUpperCase()}" created.` });
      setNewCode({ code: '', type: 'percentage', value: 0, description: '', minPurchase: 0, maxUses: 999999, stackable: false, expiresAt: '' });
      setShowCreate(false);
      fetchCodes();
    } catch (err) { toast({ title: "Error", description: err.message, variant: "destructive" }); }
  };

  const toggleCode = async (code) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/promo-codes/${code}/toggle`, {
        method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast({ title: "Updated", description: `Promo code toggled.` });
      fetchCodes();
    } catch { toast({ title: "Error", description: "Failed to toggle code.", variant: "destructive" }); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/promo-codes/${code}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      toast({ title: "Deleted", description: `Promo code "${code}" deleted.` });
      fetchCodes();
    } catch { toast({ title: "Error", description: "Failed to delete code.", variant: "destructive" }); }
  };

  const typeLabel = (type) => {
    if (type === 'percentage') return '% Off';
    if (type === 'fixed') return '$ Off';
    if (type === 'free_shipping') return 'Free Shipping';
    return type;
  };

  const typeBadgeColor = (type) => {
    if (type === 'percentage') return { bg: '#fef3c7', color: '#92400e' };
    if (type === 'fixed') return { bg: '#dbeafe', color: '#1e40af' };
    if (type === 'free_shipping') return { bg: '#d1fae5', color: '#065f46' };
    return { bg: '#f3f4f6', color: '#374151' };
  };

  if (loading) return <p className="text-center py-10" style={{ color: '#736c64' }}>Loading promo codes...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>Promo Codes</h2>
        <Button
          data-testid="create-promo-btn"
          onClick={() => setShowCreate(!showCreate)}
          className="text-white font-semibold"
          style={{ backgroundColor: '#FF8C19' }}
        >
          <Plus className="h-4 w-4 mr-2" /> New Promo Code
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <form onSubmit={createCode} className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4" style={{ borderColor: '#FF8C19' }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: '#4a2c1a' }}>Create Promo Code</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Code Name</label>
              <Input
                data-testid="promo-code-input"
                placeholder="e.g. LOCAL, SUMMER20"
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Type</label>
              <Select value={newCode.type} onValueChange={(val) => setNewCode({ ...newCode, type: val })}>
                <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage Off</SelectItem>
                  <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                  <SelectItem value="free_shipping">Free Shipping</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newCode.type !== 'free_shipping' && (
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>
                  {newCode.type === 'percentage' ? 'Discount %' : 'Discount $'}
                </label>
                <Input
                  data-testid="promo-value-input"
                  type="number" min="0" step="0.01"
                  value={newCode.value}
                  onChange={(e) => setNewCode({ ...newCode, value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Description</label>
              <Input
                data-testid="promo-desc-input"
                placeholder="e.g. Local Pickup - Free Shipping"
                value={newCode.description}
                onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Min Purchase ($)</label>
              <Input
                type="number" min="0" step="0.01"
                value={newCode.minPurchase}
                onChange={(e) => setNewCode({ ...newCode, minPurchase: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Max Uses</label>
              <Input
                type="number" min="1"
                value={newCode.maxUses}
                onChange={(e) => setNewCode({ ...newCode, maxUses: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#4a2c1a' }}>Expires (optional)</label>
              <Input
                type="date"
                value={newCode.expiresAt}
                onChange={(e) => setNewCode({ ...newCode, expiresAt: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <input
                data-testid="promo-stackable-toggle"
                type="checkbox"
                checked={newCode.stackable}
                onChange={(e) => setNewCode({ ...newCode, stackable: e.target.checked })}
                className="w-5 h-5 rounded accent-orange-500"
              />
              <div>
                <label className="text-sm font-medium" style={{ color: '#4a2c1a' }}>Can be combined</label>
                <p className="text-xs" style={{ color: '#736c64' }}>Allow stacking with other codes</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button type="submit" data-testid="save-promo-btn" className="text-white font-semibold" style={{ backgroundColor: '#22c55e' }}>
              Create Code
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      {/* Codes List */}
      {codes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-10 text-center">
          <Tag className="h-12 w-12 mx-auto mb-3" style={{ color: '#e5d5c0' }} />
          <p className="text-lg font-bold" style={{ color: '#4a2c1a' }}>No promo codes yet</p>
          <p style={{ color: '#736c64' }}>Create your first promo code above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map((c) => {
            const badge = typeBadgeColor(c.type);
            return (
              <div
                key={c.code}
                data-testid={`promo-row-${c.code}`}
                className="bg-white rounded-lg shadow-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-l-4 transition-all hover:shadow-md"
                style={{ borderColor: c.isActive ? '#22c55e' : '#dc2626', opacity: c.isActive ? 1 : 0.6 }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-lg font-bold font-mono" style={{ color: '#4a2c1a' }}>{c.code}</span>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: badge.bg, color: badge.color }}
                    >
                      {typeLabel(c.type)}
                      {c.type !== 'free_shipping' && ` (${c.type === 'percentage' ? c.value + '%' : '$' + c.value})`}
                    </span>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: c.isActive ? '#d1fae5' : '#fee2e2', color: c.isActive ? '#065f46' : '#991b1b' }}
                    >
                      {c.isActive ? 'Active' : 'Disabled'}
                    </span>
                    {c.stackable && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}>
                        Stackable
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: '#736c64' }}>
                    {c.description || 'No description'}
                    {' | '}Used: {c.usedCount || 0}/{c.maxUses === 999999 ? 'Unlimited' : c.maxUses}
                    {c.minPurchase > 0 && ` | Min: $${c.minPurchase}`}
                    {c.expiresAt && ` | Expires: ${new Date(c.expiresAt).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`toggle-promo-${c.code}`}
                    variant="outline" size="sm"
                    onClick={() => toggleCode(c.code)}
                    className="border-2"
                    style={{ borderColor: c.isActive ? '#dc2626' : '#22c55e', color: c.isActive ? '#dc2626' : '#22c55e' }}
                  >
                    {c.isActive ? <ToggleRight className="h-4 w-4 mr-1" /> : <ToggleLeft className="h-4 w-4 mr-1" />}
                    {c.isActive ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    data-testid={`delete-promo-${c.code}`}
                    variant="outline" size="sm"
                    onClick={() => deleteCode(c.code)}
                    className="border-2"
                    style={{ borderColor: '#dc2626', color: '#dc2626' }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromoCodesTab;
